'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ServiceAreaMapProps {
  className?: string;
}

const ServiceAreaMap = ({ className = '' }: ServiceAreaMapProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('fr');

  useEffect(() => {
    setIsHydrated(true);
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'fr';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    fr: {
      title: 'Zone de service',
      subtitle: 'Nous servons Le Locle et les régions environnantes',
      mainArea: 'Le Locle',
      coverage: 'Zone de couverture principale',
      surrounding: 'Régions environnantes',
      areas: [
        'La Chaux-de-Fonds',
        'Les Brenets',
        'Le Cerneux-Péquignot',
        'La Brévine',
      ],
      contact: 'Contactez-nous pour vérifier la disponibilité',
      phone: '+41 76 212 65 06',
    },
    de: {
      title: 'Servicebereich',
      subtitle: 'Wir bedienen Le Locle und die umliegenden Regionen',
      mainArea: 'Le Locle',
      coverage: 'Hauptabdeckungsbereich',
      surrounding: 'Umliegende Regionen',
      areas: [
        'La Chaux-de-Fonds',
        'Les Brenets',
        'Le Cerneux-Péquignot',
        'La Brévine',
      ],
      contact: 'Kontaktieren Sie uns, um die Verfügbarkeit zu überprüfen',
      phone: '+41 76 212 65 06',
    },
  };

  const currentContent = isHydrated ? content[currentLanguage as keyof typeof content] : content.fr;

  return (
    <section className={`py-16 lg:py-24 bg-surface ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            {currentContent.title}
          </h2>
          <p className="text-lg text-muted-foreground">
            {currentContent.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Map */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg">
            <div className="aspect-[4/3] w-full">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="Le Locle Service Area"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=47.0571,6.7491&z=12&output=embed"
                className="w-full h-full"
              />
            </div>
          </div>

          {/* Service Areas Info */}
          <div className="space-y-6">
            {/* Main Area */}
            <div className="bg-card border border-border rounded-xl p-6 shadow-md">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="MapPinIcon" size={24} variant="solid" className="text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                    {currentContent.mainArea}
                  </h3>
                  <p className="text-muted-foreground mb-3">
                    {currentContent.coverage}
                  </p>
                  <div className="flex items-center gap-2 text-success">
                    <Icon name="CheckCircleIcon" size={20} variant="solid" />
                    <span className="text-sm font-medium">Service disponible 24/7</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Surrounding Areas */}
            <div className="bg-card border border-border rounded-xl p-6 shadow-md">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="MapIcon" size={24} variant="solid" className="text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                    {currentContent.surrounding}
                  </h3>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {currentContent.areas.map((area, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-3 bg-surface rounded-lg"
                  >
                    <Icon name="CheckIcon" size={16} variant="solid" className="text-primary" />
                    <span className="text-sm font-medium text-foreground">{area}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gradient-to-br from-primary to-secondary rounded-xl p-6 text-primary-foreground">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary-foreground/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="PhoneIcon" size={24} variant="solid" className="text-primary-foreground" />
                </div>
                <div>
                  <p className="text-sm mb-2 opacity-90">
                    {currentContent.contact}
                  </p>
                  <a
                    href={`tel:${currentContent.phone.replace(/\s/g, '')}`}
                    className="text-2xl font-heading font-bold hover:opacity-80 transition-opacity duration-300"
                  >
                    {currentContent.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;